import chroma from 'chroma-js';
import { roundTo, color2css } from '../../../../../utilities';
import { componentNames } from '../components.constants';

import { IComponent } from '../IComponent';
import { IRenderer } from '../IRenderer';

/**
 *  what we actually want to achieve:
 *
 *  1. step: figure out the 3 available spaces for placing a dataLabel [- space, actual bar, + space]
 *  2. step: select one space (including inside/outside rules) that fits the label
 *  3. step: TODO
 *
 */

interface DataLabelProps {
  enabled: boolean;
  position: string;
  lightnessThreshold: number;
  format: any; // Format;
  style: any; // FontFormat;
  darkColor: string;
  lightColor: string;
  forceDraw: boolean;
  forceSign?: boolean; // deprecated
  quillValue: any;
  valueAlignment: string;
}

interface DataLabelRenderContext {
  mode: 'vertical' | 'horizontal';
  value: number; // render value
  htmlStr: string; // quill container & content
  actualSize: {
    width: number;
    height: number;
  };
  availableWidth: number;
  availableHeight: number;
  textRotate: boolean;
  color: string;
  backgroundColor: string;
  ignoreColor?: boolean;
  // bar props
  x: number;
  y: number;
  width: number;
  height: number;
  labelWidth: number;
  labelHeight: number;
  chartWidth: number;
  chartHeight: number;
  zeroX: number;
  zeroY: number;
  dataLabelOffset: any;
  callbacks: any; // reference
  valuePosition: string;
  positionAltInside: string;
  positionAltOutside: string;
  animation: {
    enabled: boolean;
    duration: number;
    delay: number;
  };
  waterfall?: boolean;
  waterfallAcc?: number;
  waterfallAccScaled?: number;
  flex?: string; // explicit flex alignment. Used in Stack Bar charts
}

const alignmentToFlex = (alignment, rotation = 'top') => {
  const matrix = {
    top: {
      'top-left': ['flex-start', 'flex-start'],
      'top-center': ['center', 'flex-start'],
      'top-right': ['flex-end', 'flex-start'],
      'center-left': ['flex-start', 'center'],
      center: ['center', 'center'],
      'center-right': ['flex-end', 'center'],
      'bottom-left': ['flex-start', 'flex-end'],
      'bottom-center': ['center', 'flex-end'],
      'bottom-right': ['flex-end', 'flex-end'],
    },
    bottom: {
      'top-left': ['flex-start', 'flex-end'],
      'top-center': ['center', 'flex-end'],
      'top-right': ['flex-end', 'flex-end'],
      'center-left': ['flex-start', 'center'],
      center: ['center', 'center'],
      'center-right': ['flex-end', 'center'],
      'bottom-left': ['flex-start', 'flex-start'],
      'bottom-center': ['center', 'flex-start'],
      'bottom-right': ['flex-end', 'flex-start'],
    },
    left: {
      'top-left': ['flex-end', 'flex-start'],
      'top-center': ['flex-end', 'center'],
      'top-right': ['flex-end', 'flex-end'],
      'center-left': ['center', 'flex-start'],
      center: ['center', 'center'],
      'center-right': ['center', 'flex-end'],
      'bottom-left': ['flex-start', 'flex-start'],
      'bottom-center': ['flex-start', 'center'],
      'bottom-right': ['flex-start', 'flex-end'],
    },
    right: {
      'top-left': ['flex-start', 'flex-start'],
      'top-center': ['flex-start', 'center'],
      'top-right': ['flex-start', 'flex-end'],
      'center-left': ['center', 'flex-start'],
      center: ['center', 'center'],
      'center-right': ['center', 'flex-end'],
      'bottom-left': ['flex-end', 'flex-start'],
      'bottom-center': ['flex-end', 'center'],
      'bottom-right': ['flex-end', 'flex-end'],
    },
  };

  const [justifyContent, alignItems] = matrix[rotation][alignment];
  return { justifyContent, alignItems };
};

const isTextInside = (textWidth, textHeight, areaWidth, areaHeight, isRotated: boolean = false): boolean => {
  if (isRotated) {
    return textWidth < areaHeight && textHeight < areaWidth;
  } else {
    return textWidth < areaWidth && textHeight < areaHeight;
  }
};

export class DataLabel implements IComponent {
  componentName: componentNames.datalabel;
  props: DataLabelProps;

  protected constructor(props: DataLabelProps) {
    this.props = props;
  }

  public getChildren(): IComponent[] {
    return [];
  }

  public toJson() {
    throw new Error('Method not implemented.');
  }

  public render(renderer: IRenderer) {
    return renderer.render();
  }

  static create = (props: DataLabelProps) => {
    return new DataLabel(props);
  };

  public quillToHTML = () => {
    //
  };

  public getRenderConfig = (context: DataLabelRenderContext) => {
    const {
      mode,
      actualSize,
      htmlStr,
      value,
      availableWidth,
      availableHeight,
      textRotate,
      color,
      backgroundColor,
      x,
      y,
      width,
      labelWidth,
      labelHeight,
      chartWidth,
      chartHeight,
      zeroX,
      zeroY,
      dataLabelOffset,
      callbacks,
      valuePosition,
      positionAltInside,
      positionAltOutside, // Only used on StackBarChart
      animation,
      ignoreColor,
      waterfall,
      waterfallAcc,
      waterfallAccScaled,
      flex,
    } = context;
    let { height } = context;
    const { lightnessThreshold, darkColor, lightColor, valueAlignment, forceDraw } = this.props;

    // if we can't fit the width or height, abort
    // console.log(mode, 'width', labelWidth, width, 'height', labelHeight, height);
    if (mode === 'vertical' && labelWidth - 1 > width && !forceDraw) return;
    if (mode === 'horizontal' && labelHeight - 1 > height && !forceDraw) return;

    let isInside = valuePosition.includes('inside') && labelWidth - 1 <= width && labelHeight - 1 <= height;

    const isZero = isInside ? valuePosition.includes('inside-zero') : valuePosition.includes('alt-zero') || valuePosition.includes('outside-zero');

    if (positionAltInside === 'stack' && !isInside && !forceDraw) return;
    if (positionAltInside === 'stack' && forceDraw) isInside = true;

    // console.log(valueStr, roundTo(actualSize.width), roundTo(actualSize.height), roundTo(width), roundTo(height), isInside, isOutside);

    const isContrastLight = chroma(isInside ? color : backgroundColor).hsl()[2] > lightnessThreshold;
    const textColor = isContrastLight ? darkColor : lightColor;

    // start
    let xO = roundTo(x);
    let yO = roundTo(y);

    let xA, yA, rectWidth, rectHeight, rotation;

    if (mode === 'vertical') {
      // offset
      const offset = isZero ? (value < 0 ? dataLabelOffset.top : dataLabelOffset.bottom) : value >= 0 ? dataLabelOffset.top : dataLabelOffset.bottom;

      // animated
      xA = roundTo(x);
      yA = roundTo(isInside ? y - height : value >= 0 ? -dataLabelOffset.top : y);

      // size
      rectWidth = roundTo(width);
      rectHeight = roundTo(isInside ? height : availableHeight - height + offset);

      // waterfall
      if (waterfall) {
        switch (valuePosition) {
          case 'outside-away':
            if (value > 0) {
              if ((waterfallAcc || 0) + value >= 0) rectHeight = availableHeight - (waterfallAccScaled || 0) + offset;
              else rectHeight = availableHeight + (waterfallAccScaled || 0) + offset;
            }
            break;
          case 'inside-away-alt-away':
          case 'inside-zero-alt-away':
            if (!isInside) {
              rectHeight = y - height;
            }
            break;
          case 'inside-away-alt-zero':
          case 'inside-zero-alt-zero':
          case 'outside-zero':
            break;
          default:
            break;
        }
      }

      if (!isInside && isZero) {
        yA = value >= 0 ? zeroY : -dataLabelOffset.top;
        rectHeight = value >= 0 ? chartHeight - zeroY + offset : zeroY + offset;
        if (waterfall && value < 0) {
          rectHeight = chartHeight - zeroY + offset;
          yA = zeroY;
        }
      }

      // alignment
      rotation = 'top';
      if (textRotate) rotation = 'left';
      if (value < 0) {
        if (rotation === 'top') rotation = 'bottom';
        if (rotation === 'left') rotation = 'right';
      }

      // flip rotation// if (!isInside && value >= 0) height = availableHeight - y + 10;
      if (!isInside && !isZero) {
        if (value < 0) {
          if (rotation === 'bottom') rotation = 'top';
          if (rotation === 'right') rotation = 'left';
        } else {
          if (rotation === 'left') rotation = 'right';
          if (rotation === 'top') rotation = 'bottom';
        }
      }
    } else {
      // offset
      const offset = isZero ? (value < 0 ? dataLabelOffset.right : dataLabelOffset.left) : value >= 0 ? dataLabelOffset.right : dataLabelOffset.left;

      // animated
      xA = roundTo(isInside ? x : value >= 0 ? x + width : -dataLabelOffset.left);
      yA = roundTo(y);

      // size
      rectWidth = roundTo(isInside ? width : availableWidth - width + offset);
      rectHeight = roundTo(height);

      // waterfall
      if (waterfall) {
        switch (valuePosition) {
          case 'outside-away':
            if (value < 0) {
              xA = x - labelWidth;
              rectWidth = labelWidth;
            }
            break;
          case 'inside-away-alt-away':
          case 'inside-zero-alt-away':
            if (!isInside && value < 0) {
              xA = x - labelWidth;
            }
            break;
          case 'inside-away-alt-zero':
          case 'inside-zero-alt-zero':
          case 'outside-zero':
            break;
          default:
            break;
        }
      }

      if (!isInside && isZero) {
        xA = value >= 0 ? -dataLabelOffset.left : zeroX;
        rectWidth = value >= 0 ? zeroX + offset : chartWidth - zeroX + offset;
        if (waterfall && value < 0) {
          rectWidth = zeroX + offset;
          xA = -dataLabelOffset.left;
        }
      }

      // alignment
      rotation = 'left';
      if (value < 0) {
        if (rotation === 'left') rotation = 'right';
      }

      // flip rotation
      if (!isInside && !isZero) {
        if (value < 0) {
          if (rotation === 'right') rotation = 'left';
        } else {
          if (rotation === 'left') rotation = 'right';
        }
      }

      if (isInside && value > 0) {
        xO -= rectWidth;
      }
    }

    const alignment = isInside && isZero ? 'bottom-center' : 'top-center'; // valueAlignment;

    // console.log('height', rectHeight, height, 'width', rectWidth, width);
    // console.log('inside', isInside, 'zero', isZero, valuePosition, alignment, rotation);
    if (mode === 'vertical' && waterfall && isZero && value < 0) rotation = 'top'; // Make all uniform
    if (mode === 'horizontal' && waterfall && value < 0) rotation = 'right'; // Make all uniform
    let { justifyContent, alignItems } = alignmentToFlex(alignment, rotation);

    if (flex) {
      if (mode === 'horizontal') justifyContent = flex;
      if (mode === 'vertical') alignItems = flex;
    }

    const updatedStyles: string[] = [];
    const contrastPattern = isContrastLight ? /dark-(color:(?:#[0-9a-f]+|rgba?\([^\)]+\)))/g : /light-(color:(?:#[0-9a-f]+|rgba?\([^\)]+\)))/g;
    const contrastMatch = htmlStr.match(contrastPattern);
    if (contrastMatch?.[1]) {
      updatedStyles.push(contrastMatch[1]);
    }

    const backgroundPattern = /(background-color:(?:#[0-9a-f]+|rgba?\([^\)]+\)))/g;
    const backgroundMatch = htmlStr.match(backgroundPattern);
    if (backgroundMatch?.[1]) {
      updatedStyles.push(backgroundMatch[1]);
    }

    let idx = 0;
    const replaceFn = (match, ...args) => {
      if (match.includes('background') && backgroundMatch && backgroundMatch[1]) return `${backgroundMatch[1]};`;
      if (contrastMatch && idx < contrastMatch.length) return `${contrastMatch[idx++].replace(/(dark-|light-)/g, '')};`;

      return '';
    };

    // (?<!background-)
    const noColorStr = htmlStr.replace(/(([";])color:(?:#[0-9a-f]+|rgba?\([^\)]+\));?)+/g, '$2');
    const fixedColorStr = noColorStr.replace(/((?:dark-|light-|background-)?color:(?:#[0-9a-f]+|rgba?\([^\)]+\));?)+/g, replaceFn); // updatedStyles.join(';') + ';

    // console.log('ignoreColor', ignoreColor);
    // console.log('updatedStyles', updatedStyles);
    // console.log('htmlStr', htmlStr);
    // console.log('noColorStr', noColorStr);
    // console.log('fixedColorStr', fixedColorStr);

    const labelData = {
      html: ignoreColor ? htmlStr : fixedColorStr,
      x: xO,
      y: yO,
      width: rectWidth, // mode === 'vertical' ? rectWidth : 0,
      height: rectHeight, // mode === 'horizontal' ? rectHeight : 0,
      color: color2css(textColor),
      flex: mode === 'horizontal' ? '1 1 auto' : undefined,
      callbacks,
      justifyContent,
      alignItems,
      writingMode: textRotate ? 'vertical-rl' : undefined,
      transform: textRotate ? `translate(${xO}px, ${yO}px) rotate(180deg)` : `translate(${xO}px, ${yO}px)`,
      transformForce: textRotate ? 'rotate(180deg)' : undefined,
      animate: {
        enabled: animation.enabled,
        delay: animation.delay,
        duration: animation.duration,
        width: rectWidth,
        height: rectHeight,
        writingMode: textRotate ? 'vertical-rl' : undefined,
        transform: textRotate ? `translate(${xA}px, ${yA}px) rotate(180deg)` : `translate(${xA}px, ${yA}px)`,
      },
    };

    return labelData;
  };
}
